import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, X, AlertCircle } from 'lucide-react';
import type { Job, FtpProtocol } from '@atlas/shared';
import { StatusBadge } from './StatusBadge.js';
import { SourceBadge } from './SourceBadge.js';
import { explainJobError } from '../lib/jobErrors.js';

const PROTOCOL_LABELS: Record<FtpProtocol, string> = {
  ftp: 'FTP (plain)',
  ftps: 'FTPS (implicit TLS)',
  sftp: 'SFTP (SSH)',
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-[#aeaaa4] hover:text-[#37352f] transition-colors shrink-0"
      title={copied ? 'Copied' : 'Copy S3 location'}
    >
      {copied ? <Check size={13} className="text-[#0f7b6c]" /> : <Copy size={13} />}
    </button>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[11px] uppercase tracking-wide text-[#aeaaa4]">
        {label}
      </span>
      <span
        className={`text-sm text-[#37352f] break-words ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

export function JobDetailModal({
  job,
  onClose,
}: {
  job: Job & { id: string };
  onClose: () => void;
}) {
  const info = job.status === 'failed' ? explainJobError(job.error) : null;
  const protocolLabel = job.ftp
    ? PROTOCOL_LABELS[job.ftp.protocol] ?? job.ftp.protocol
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[520px] max-w-[95vw] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#e9e9e7]">
          <div className="flex flex-col gap-2 min-w-0">
            <h2 className="text-base font-semibold text-[#37352f] truncate">
              {job.clientName}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <SourceBadge system={job.sourceSystem} />
              <StatusBadge status={job.status} error={job.error} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#aeaaa4] hover:text-[#37352f] p-1 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* How to fix (failed jobs only) */}
        {info && (
          <div className="m-5 rounded-lg border border-[#f3c9c9] bg-[#fbe8e8] p-4">
            <div className="flex items-center gap-1.5 text-[#c93232] font-medium text-sm">
              <AlertCircle size={14} className="shrink-0" />
              How to fix this
            </div>
            <p className="mt-1.5 text-sm text-[#37352f]">{info.summary}</p>
            <ol className="mt-2 list-decimal pl-5 text-sm text-[#37352f] space-y-1">
              {info.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {job.error && (
              <p className="mt-3 text-[11px] text-[#a02828] font-mono break-words">
                Raw error: {job.error}
              </p>
            )}
          </div>
        )}

        {/* Source setup — FTP details, or a download-link note for URL sources. */}
        <div className="p-5 pt-0">
          {job.ftp && protocolLabel ? (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#73726e] mb-3 mt-5">
                FTP setup
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Protocol" value={protocolLabel} />
                <Field label="Port" value={String(job.ftp.port)} />
                <Field label="Host" value={job.ftp.host} mono />
                <Field label="Username" value={job.ftp.username} mono />
                <div className="col-span-2">
                  <Field label="Path" value={job.ftp.path || '/'} mono />
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#73726e] mb-3 mt-5">
                Source
              </h3>
              <Field
                label="Intake"
                value="Download link (fetched into S3 by the worker)"
              />
            </>
          )}

          <h3 className="text-xs font-semibold uppercase tracking-wide text-[#73726e] mb-3 mt-6">
            Destination
          </h3>
          <div className="flex items-end justify-between gap-2 min-w-0">
            <Field
              label="S3 location"
              value={
                job.s3Destination
                  ? `s3://${job.s3Destination.bucket}/${job.s3Destination.prefix}`
                  : '—'
              }
              mono
            />
            {job.s3Destination && (
              <CopyButton
                value={`s3://${job.s3Destination.bucket}/${job.s3Destination.prefix}`}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
